// Transport — resolves the active host (the real injected window.GammaSDK or
// the dev mock) and turns its callback-style methods into promises.

import { createMockHost } from './mock';
import type { BridgeResult, GammaConfig, HostSdk } from './types';

const DEFAULT_READY_TIMEOUT_MS = 1500;

interface ResolvedHost {
  host: HostSdk;
  isInApp: boolean;
}

function getInjectedHost(): HostSdk | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { GammaSDK?: HostSdk };
  const sdk = w.GammaSDK;
  return sdk && sdk.ready ? sdk : null;
}

/** True only when the dev mock is explicitly opted into via config. */
function mockEnabled(config: GammaConfig): boolean {
  return config.mock != null && config.mock !== false;
}

/**
 * Host used outside the Gamma Games app when the dev mock is NOT enabled:
 * every coin/progress call reports `no_transport`, so the integration is
 * inert and the game falls back to its own offline behaviour. This is the
 * default — the SDK is only "effective" inside the app.
 */
function createInertHost(): HostSdk {
  const deny = (cb: (r: BridgeResult) => void) =>
    cb({ success: false, error: 'no_transport' });
  return {
    ready: false,
    version: 'inert',
    getCoins: (cb) => deny(cb),
    spendCoins: (_a, _b, cb) => deny(cb),
    earnCoins: (_a, _b, cb) => deny(cb),
    saveProgress: (_d, cb) => deny(cb),
    loadProgress: (cb) => deny(cb),
    getPlayer: (cb) => deny(cb),
    getCoinValue: (cb) => deny(cb),
    getChallenge: (cb) => deny(cb),
    submitChallengeScore: (_v, cb) => deny(cb),
    completeChallenge: (_r, cb) => deny(cb),
  };
}

/** The host to use when no real bridge is present. */
function fallbackHost(config: GammaConfig): HostSdk {
  return mockEnabled(config) ? createMockHost(config) : createInertHost();
}

/**
 * Waits for the real host to be injected, up to `readyTimeoutMs`. Resolves
 * with the injected host (`isInApp: true`) as soon as it appears or the
 * `gammasdkready` event fires; otherwise resolves with the dev mock.
 */
export function resolveHost(config: GammaConfig): Promise<ResolvedHost> {
  const timeout = config.readyTimeoutMs ?? DEFAULT_READY_TIMEOUT_MS;

  return new Promise((resolve) => {
    const existing = getInjectedHost();
    if (existing) {
      resolve({ host: existing, isInApp: true });
      return;
    }

    if (typeof window === 'undefined') {
      // SSR / non-browser: nothing to wait for.
      resolve({ host: fallbackHost(config), isInApp: false });
      return;
    }

    let settled = false;
    const finish = (resolved: ResolvedHost) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('gammasdkready', onReady);
      clearTimeout(timer);
      resolve(resolved);
    };

    const onReady = () => {
      const host = getInjectedHost();
      if (host) finish({ host, isInApp: true });
    };

    const timer = setTimeout(() => {
      // Host never showed up — we're outside the app. Use the inert host
      // (or the dev mock, only if explicitly enabled) so the game still runs.
      finish({ host: fallbackHost(config), isInApp: false });
    }, timeout);

    window.addEventListener('gammasdkready', onReady);
  });
}

/** Promisify a host method that takes (...args, callback). */
export function call(
  host: HostSdk,
  method: keyof HostSdk,
  args: unknown[],
): Promise<BridgeResult> {
  return new Promise((resolve) => {
    const fn = host[method];
    if (typeof fn !== 'function') {
      // Older host that predates this method (e.g. getCoinValue on <1.1.0).
      resolve({ success: false, error: 'unknown_type' });
      return;
    }
    try {
      (fn as (...a: unknown[]) => void).apply(host, [
        ...args,
        (r: BridgeResult) => resolve(r ?? { success: false, error: 'internal_error' }),
      ]);
    } catch {
      resolve({ success: false, error: 'internal_error' });
    }
  });
}
